import { useState, type FormEvent } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { Alerte, Bouton, Carte } from '../ui';
import { ChampMotDePasse } from '../ui/ChampMotDePasse';

/** Même plancher que le serveur (`auth.dto.ts`) : le refuser ici évite un aller-retour. */
const LONGUEUR_MINIMALE = 12;

/**
 * Choix d'un nouveau mot de passe, depuis le lien reçu par e-mail.
 *
 * Le jeton arrive par l'URL, il n'est jamais ressaisi. Un lien sans jeton — recopié à la
 * main, tronqué par un client de messagerie — le dit tout de suite plutôt que de laisser
 * remplir un formulaire qui échouera à l'envoi.
 *
 * Un seul champ, sans confirmation : le bouton afficher/masquer permet de relire ce qu'on
 * a tapé, ce qu'une seconde saisie à l'aveugle ne permet pas. Deux champs masqués
 * attrapent surtout les fautes qu'on répète.
 */
export function ReinitialiserMotDePasse({
  jeton,
  surConnexion,
}: {
  jeton: string;
  surConnexion?: () => void;
}) {
  const { client } = useSession();
  const [motDePasse, setMotDePasse] = useState('');
  const [fait, setFait] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  if (!jeton) {
    return (
      <Carte titre="Ce lien est incomplet">
        <p style={{ marginTop: 0 }}>
          Ouvrez-le directement depuis l'e-mail reçu, sans le recopier : certains clients de
          messagerie le coupent en deux.
        </p>
        <Bouton variante="secondaire" onClick={() => surConnexion?.()}>
          Revenir à la connexion
        </Bouton>
      </Carte>
    );
  }

  if (fait) {
    return (
      <Carte titre="Votre mot de passe est changé">
        <p style={{ marginTop: 0 }}>
          Vous pouvez vous connecter avec le nouveau. Les sessions ouvertes ailleurs ont été
          fermées.
        </p>
        <Bouton onClick={() => surConnexion?.()}>Se connecter</Bouton>
      </Carte>
    );
  }

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault();
    setErreur(null);

    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(`Le mot de passe doit faire au moins ${LONGUEUR_MINIMALE} caractères.`);
      return;
    }

    setEnvoi(true);
    try {
      await client.request('/auth/password/reset', {
        method: 'POST',
        body: { token: jeton, password: motDePasse },
        skipRefresh: true,
      });
      setFait(true);
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <Carte titre="Choisir un nouveau mot de passe">
      <form onSubmit={(evenement) => void soumettre(evenement)} noValidate>
        <ChampMotDePasse
          label="Nouveau mot de passe"
          name="password"
          autoComplete="new-password"
          required
          value={motDePasse}
          onChange={(evenement) => setMotDePasse(evenement.target.value)}
          aide={`Au moins ${LONGUEUR_MINIMALE} caractères. Une phrase dont vous vous souvenez fait un bon mot de passe.`}
        />

        {erreur ? <Alerte titre="Le mot de passe n’a pas été changé">{erreur}</Alerte> : null}

        <div className="actions-empilees">
          <Bouton type="submit" disabled={!motDePasse || envoi}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      </form>
    </Carte>
  );
}
